<template>
  <div
    class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-5"
  >
    <div
      class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between"
    >
      <div>
        <p
          class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
        >
          Preview
        </p>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-1">
          {{ reportLabel }}
        </h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {{ previewDescription }}
        </p>
      </div>
      <span
        class="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
        :class="statusClasses"
      >
        {{ statusLabel }}
      </span>
    </div>

    <div
      v-if="generatedAtLabel || downloadOptions.length"
      class="flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400"
    >
      <span
        v-if="generatedAtLabel"
        class="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-900 px-3 py-1"
      >
        Generated: {{ generatedAtLabel }}
      </span>
      <span
        v-if="downloadOptions.length"
        class="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-900 px-3 py-1"
      >
        {{ downloadOptions.length }} download option{{
          downloadOptions.length === 1 ? "" : "s"
        }}
        available
      </span>
    </div>

    <div
      v-if="!task"
      class="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60 p-5 text-sm text-gray-500 dark:text-gray-400"
    >
      Run a report to see its preview here. Downloads are created only when you
      choose a format.
    </div>

    <div
      v-else-if="isFailed"
      class="rounded-lg border border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-950/30 p-5 text-sm text-red-700 dark:text-red-300"
    >
      {{ failureMessage }}
    </div>

    <div
      v-else-if="isRunning"
      class="rounded-lg border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50 dark:bg-emerald-950/20 p-5 text-sm text-emerald-700 dark:text-emerald-300"
    >
      <div class="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div
          class="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white text-emerald-600 shadow-sm dark:bg-emerald-950/40 dark:text-emerald-300"
        >
          <span
            class="h-6 w-6 rounded-full border-2 border-emerald-300 border-t-emerald-600 animate-spin dark:border-emerald-800 dark:border-t-emerald-300"
          ></span>
        </div>
        <div class="min-w-0 flex-1">
          <p
            class="text-sm font-semibold text-emerald-900 dark:text-emerald-100"
          >
            Preparing preview
          </p>
          <p class="mt-1 text-sm text-emerald-700 dark:text-emerald-300">
            The preview is on the way. Download options appear after this run
            finishes.
          </p>
          <div class="mt-3 flex items-center gap-2">
            <span
              class="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse"
            ></span>
            <span
              class="h-2.5 w-2.5 rounded-full bg-emerald-400/80 animate-pulse"
            ></span>
            <span
              class="h-2.5 w-2.5 rounded-full bg-emerald-300/70 animate-pulse"
            ></span>
          </div>
        </div>
      </div>
    </div>

    <template v-else>
      <div
        v-if="summaryItems.length"
        class="grid gap-3 md:grid-cols-2 xl:grid-cols-3"
      >
        <div
          v-for="item in summaryItems"
          :key="item.label"
          class="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4"
        >
          <p
            class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
          >
            {{ item.label }}
          </p>
          <p
            class="mt-2 text-lg font-semibold text-gray-900 dark:text-gray-100"
          >
            {{ formatValue(item.value) }}
          </p>
        </div>
      </div>

      <div v-if="previewSections.length" class="space-y-4">
        <section
          v-for="section in previewSections"
          :key="section.title"
          class="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
        >
          <div
            class="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-4 py-3"
          >
            <div
              class="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <p
                  class="text-sm font-semibold text-gray-900 dark:text-gray-100"
                >
                  {{ section.title }}
                </p>
                <p
                  v-if="section.description"
                  class="mt-1 text-xs text-gray-500 dark:text-gray-400"
                >
                  {{ section.description }}
                </p>
              </div>
              <p
                v-if="section.row_count != null"
                class="text-xs text-gray-500 dark:text-gray-400"
              >
                {{ section.row_count }} row{{
                  section.row_count === 1 ? "" : "s"
                }}
              </p>
            </div>
          </div>

          <div
            v-if="section.rows && section.rows.length"
            class="overflow-x-auto"
          >
            <table
              class="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm"
            >
              <thead class="bg-white dark:bg-gray-800">
                <tr>
                  <th
                    v-for="header in section.headers || []"
                    :key="header.key"
                    scope="col"
                    class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                  >
                    {{ header.label || header.key }}
                  </th>
                </tr>
              </thead>
              <tbody
                class="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800"
              >
                <tr
                  v-for="(row, rowIndex) in section.rows"
                  :key="`${section.title}-${rowIndex}`"
                >
                  <td
                    v-for="header in section.headers || []"
                    :key="`${section.title}-${rowIndex}-${header.key}`"
                    class="px-4 py-3 align-top text-gray-700 dark:text-gray-200"
                  >
                    {{ formatValue(row?.[header.key]) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-else
            class="px-4 py-4 text-sm text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800"
          >
            {{
              section.empty_message ||
              "No data available for this preview section."
            }}
          </div>

          <div
            v-if="section.has_more"
            class="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-4 py-3 text-xs text-gray-500 dark:text-gray-400"
          >
            Showing the first {{ section.rows?.length || 0 }} of
            {{ section.row_count || 0 }} rows. Download the file for the full
            report.
          </div>
        </section>
      </div>

      <div
        v-else
        class="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60 p-5 text-sm text-gray-500 dark:text-gray-400"
      >
        This report completed without an inline preview. Use a download option
        below to generate the full file when needed.
      </div>

      <div v-if="downloadOptions.length" class="space-y-3">
        <div>
          <p
            class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
          >
            Downloads
          </p>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Choose a format to generate the export only when you need it.
          </p>
        </div>
        <div class="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          <button
            v-for="(file, fileIndex) in downloadOptions"
            :key="file.filename || file.format || fileIndex"
            type="button"
            class="flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-left transition-colors hover:border-emerald-300 hover:bg-emerald-100 dark:border-emerald-900/60 dark:bg-emerald-950/20 dark:hover:bg-emerald-950/30"
            :title="file.filename || downloadDescription(file)"
            @click="$emit('download-file', file)"
          >
            <div class="flex min-w-0 items-center gap-3">
              <span
                class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-emerald-700 shadow-sm dark:bg-emerald-900/60 dark:text-emerald-200"
              >
                <vue-feather type="download" size="16" />
              </span>
              <div class="min-w-0">
                <p
                  class="text-sm font-semibold text-emerald-900 dark:text-emerald-100"
                >
                  {{ downloadLabel(file, fileIndex) }}
                </p>
                <p class="mt-1 text-xs text-emerald-700 dark:text-emerald-300">
                  {{ downloadDescription(file) }}
                </p>
              </div>
            </div>
            <div class="flex shrink-0 flex-col items-end gap-2">
              <span
                class="inline-flex rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold tracking-wide text-emerald-700 shadow-sm dark:bg-emerald-900/60 dark:text-emerald-200"
              >
                {{ normalizedFileFormat(file) }}
              </span>
              <span
                class="inline-flex items-center gap-1 text-[11px] font-semibold tracking-wide text-emerald-700 dark:text-emerald-200"
              >
                <vue-feather type="download" size="12" />
                Download
              </span>
            </div>
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import VueFeather from "vue-feather";

export default {
  name: "StoreReportPreviewPanel",
  components: {
    VueFeather,
  },
  props: {
    task: {
      type: Object,
      default: null,
    },
    reportConfig: {
      type: Object,
      default: null,
    },
  },
  emits: ["download-file"],
  computed: {
    reportLabel() {
      return (
        this.task?.data?.reportLabel ||
        this.reportConfig?.label ||
        this.task?.data?.reportType ||
        "Store report"
      );
    },
    previewDescription() {
      if (this.task && this.isRunning) {
        return "Preparing a preview for this report.";
      }
      return (
        this.reportConfig?.description ||
        "Preview first, then generate the export only when you need the full file."
      );
    },
    taskResult() {
      return this.task?.data?.result || null;
    },
    previewPayload() {
      return this.taskResult?.preview || null;
    },
    summaryItems() {
      const summary = this.previewPayload?.summary;
      return Array.isArray(summary) ? summary : [];
    },
    previewSections() {
      const sections = this.previewPayload?.sections;
      return Array.isArray(sections) ? sections : [];
    },
    generatedFiles() {
      const files = this.taskResult?.files;
      return Array.isArray(files) ? files : [];
    },
    requestedFormats() {
      const resultFormats = this.taskResult?.requested_formats;
      if (Array.isArray(resultFormats) && resultFormats.length) {
        return resultFormats;
      }

      const taskFormat = this.task?.data?.reportFormat;
      return taskFormat ? [taskFormat] : [];
    },
    downloadOptions() {
      if (this.generatedFiles.length) {
        return this.generatedFiles;
      }

      if (!this.task || this.isRunning || this.isFailed) {
        return [];
      }

      const formats = [];
      this.requestedFormats.forEach((formatKey) => {
        const normalized = String(formatKey || "")
          .trim()
          .toUpperCase();
        if (!normalized) {
          return;
        }

        if (normalized === "BOTH") {
          (this.reportConfig?.formats || ["PDF", "XLSX"]).forEach(
            (allowedFormat) => {
              const normalizedAllowed = String(allowedFormat || "")
                .trim()
                .toUpperCase();
              if (normalizedAllowed && normalizedAllowed !== "BOTH") {
                formats.push({ format: normalizedAllowed, manual: true });
              }
            },
          );
          return;
        }

        formats.push({ format: normalized, manual: true });
      });

      if (!formats.length && this.reportConfig?.defaultFormat) {
        formats.push({
          format: String(this.reportConfig.defaultFormat).trim().toUpperCase(),
          manual: true,
        });
      }

      const seen = new Set();
      return formats.filter((entry) => {
        const key = entry.format;
        if (!key || seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
    },
    generatedAtLabel() {
      const rawValue =
        this.previewPayload?.generated_at ||
        this.taskResult?.generated_at ||
        this.task?.startedAt;
      return this.formatDateTime(rawValue);
    },
    statusValue() {
      return String(this.task?.status || "").toUpperCase();
    },
    isRunning() {
      return ["PENDING", "STARTED", "PROGRESS", "RETRY"].includes(
        this.statusValue,
      );
    },
    isFailed() {
      return ["FAILURE", "FAILED", "ERROR"].includes(this.statusValue);
    },
    statusLabel() {
      if (!this.task) {
        return "No preview yet";
      }
      if (this.isRunning) {
        return "Preparing";
      }
      if (this.isFailed) {
        return "Failed";
      }
      if (this.previewSections.length) {
        return "Preview ready";
      }
      if (this.downloadOptions.length) {
        return "Ready to download";
      }
      return this.statusValue || "Ready";
    },
    statusClasses() {
      if (!this.task) {
        return "bg-gray-100 text-gray-600 dark:bg-gray-900 dark:text-gray-300";
      }
      if (this.isRunning) {
        return "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300";
      }
      if (this.isFailed) {
        return "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300";
      }
      return "bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300";
    },
    failureMessage() {
      return (
        this.task?.data?.error ||
        this.taskResult?.error ||
        this.taskResult?.message ||
        "Report generation failed."
      );
    },
  },
  methods: {
    normalizedFileFormat(file) {
      const rawFormat = String(file?.format || "")
        .trim()
        .toUpperCase();
      if (rawFormat) {
        return rawFormat;
      }

      const fileName = String(file?.filename || "");
      const extension = fileName.split(".").pop();
      return extension ? extension.toUpperCase() : "FILE";
    },
    friendlyFormatName(file) {
      switch (this.normalizedFileFormat(file)) {
        case "XLSX":
          return "Excel";
        case "PDF":
          return "PDF";
        case "CSV":
          return "CSV";
        default:
          return "File";
      }
    },
    downloadLabel(file, fileIndex = 0) {
      const format = this.normalizedFileFormat(file);
      const sameFormatFiles = this.generatedFiles.filter(
        (entry) => this.normalizedFileFormat(entry) === format,
      );
      const formatName = this.friendlyFormatName(file);

      if (sameFormatFiles.length <= 1) {
        return `${formatName} export`;
      }

      const sequence = sameFormatFiles.findIndex(
        (entry) => entry?.filename === file?.filename,
      );
      return `${formatName} export ${
        sequence >= 0 ? sequence + 1 : fileIndex + 1
      }`;
    },
    downloadDescription(file) {
      const reportName = String(this.reportLabel || "report").trim();
      switch (this.normalizedFileFormat(file)) {
        case "XLSX":
          return `Full ${reportName} spreadsheet`;
        case "PDF":
          return `Printable ${reportName} document`;
        case "CSV":
          return `Full ${reportName} data export`;
        default:
          return `Full ${reportName} file`;
      }
    },
    formatValue(value) {
      if (value == null || value === "") {
        return "-";
      }
      if (Array.isArray(value)) {
        return value.length ? value.join(", ") : "-";
      }
      if (typeof value === "object") {
        try {
          return JSON.stringify(value);
        } catch (error) {
          return String(value);
        }
      }
      return String(value);
    },
    formatDateTime(value) {
      if (!value) {
        return "";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return String(value);
      }
      return new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }).format(date);
    },
  },
};
</script>
